// src/pages/PanelFirma/Delegados.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, TextField, Button, IconButton, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Autocomplete } from '@mui/material';

import {
    panelFirmaDelegadosJWT,
    panelFirmaAgregarDelegadoJWT,
    panelFirmaEliminarDelegadoJWT,
    getCombosJWT,
} from '../../api/endpoints';

const styles = {
    paper: {
        padding: '24px',
        maxWidth: 700,
        borderRadius: '4px',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
    },
    addRow: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginBottom: '24px',
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        padding: '32px',
    },
};

const Delegados = () => {
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [delegados, setDelegados] = useState([]);
    const [nuevoUsuario, setNuevoUsuario] = useState('');
    const [personalOptions, setPersonalOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const cargarDelegados = useCallback(async () => {
        try {
            setLoading(true);
            const data = await panelFirmaDelegadosJWT();
            if (data?.ok) setDelegados(data.delegados || []);
        } catch {
            enqueueSnackbar('Error al cargar delegados', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        cargarDelegados();
    }, [cargarDelegados]);

    const buscarPersonal = async (q) => {
        if (!q || q.length < 2) { setPersonalOptions([]); return; }
        setSearching(true);
        try {
            const data = await getCombosJWT('personal', q, 20);
            setPersonalOptions((data?.items || []).map(p => ({
                label: `${p.label || p.nombre || ''} (${p.usuario || ''})`,
                usuario: p.usuario || '',
            })));
        } catch {
            setPersonalOptions([]);
        } finally {
            setSearching(false);
        }
    };

    const handleAgregar = async () => {
        if (!nuevoUsuario.trim()) {
            enqueueSnackbar('Ingrese un usuario', { variant: 'warning' });
            return;
        }
        try {
            const data = await panelFirmaAgregarDelegadoJWT(nuevoUsuario.trim());
            if (data?.ok) {
                enqueueSnackbar('Delegado agregado', { variant: 'success' });
                setNuevoUsuario('');
                setPersonalOptions([]);
                await cargarDelegados();
            } else {
                enqueueSnackbar(data?.mensaje || 'Error al agregar delegado', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Error al agregar delegado', { variant: 'error' });
        }
    };

    const handleEliminar = async () => {
        if (!confirmDelete) return;
        try {
            const data = await panelFirmaEliminarDelegadoJWT(confirmDelete.Id);
            if (data?.ok) {
                enqueueSnackbar('Delegado eliminado', { variant: 'success' });
                setConfirmDelete(null);
                await cargarDelegados();
            }
        } catch {
            enqueueSnackbar('Error al eliminar delegado', { variant: 'error' });
        }
    };

    const formatFecha = (f) => {
        if (!f) return '-';
        try { return new Date(f).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
        catch { return f; }
    };

    return (
        <Paper sx={styles.paper}>
            <Box sx={styles.header}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Delegados autorizados
                </Typography>
                <Tooltip title="Recargar">
                    <IconButton onClick={cargarDelegados} disabled={loading}><RefreshIcon /></IconButton>
                </Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
                Las personas delegadas podrán acceder al Panel de Firma y revisar/firmar documentos en su nombre.
            </Typography>

            {/* Add new delegate */}
            <Box sx={styles.addRow}>
                <Autocomplete
                    freeSolo
                    options={personalOptions}
                    getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
                    inputValue={nuevoUsuario}
                    onInputChange={(_, v) => {
                        setNuevoUsuario(v);
                        buscarPersonal(v);
                    }}
                    onChange={(_, v) => {
                        if (v && typeof v === 'object') setNuevoUsuario(v.usuario);
                    }}
                    loading={searching}
                    style={{ width: 400 }}
                    renderInput={(params) => (
                        <TextField {...params} label="Buscar persona o ingresar usuario" placeholder="Nombre o usuario..." />
                    )}
                />
                <Button
                    variant="contained" startIcon={<PersonAddIcon />}
                    onClick={handleAgregar} disabled={!nuevoUsuario.trim()}
                >
                    Agregar
                </Button>
            </Box>

            {/* Delegates list */}
            {loading ? (
                <Box sx={styles.loadingBox}><CircularProgress /></Box>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 600 }}>Usuario</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Fecha Alta</TableCell>
                                <TableCell style={{ fontWeight: 600, textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {delegados.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No hay delegados configurados
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : delegados.map((d) => (
                                <TableRow key={d.Id} hover>
                                    <TableCell>{d.Usuario_Delegado}</TableCell>
                                    <TableCell>{formatFecha(d.Fecha_Alta)}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Eliminar delegado">
                                            <IconButton size="small" color="error" onClick={() => setConfirmDelete(d)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Confirm delete dialog */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs">
                <DialogTitle>Eliminar Delegado</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Eliminar a <strong>{confirmDelete?.Usuario_Delegado}</strong> como delegado?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={handleEliminar}>Eliminar</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default Delegados;
